/**
 * Word-level diff for incremental track changes application.
 *
 * Uses LCS (Longest Common Subsequence) to produce an optimal word-level diff,
 * then groups consecutive changes into ChangeChunks with surrounding context
 * so the caller can search-and-apply each chunk individually in Word.
 */

/* ---------- public types ---------- */

export interface DiffOp {
  type: 'equal' | 'delete' | 'insert';
  words: string[];
}

/**
 * A single contiguous region of change within the paragraph.
 *
 * - `delete`:  some old words removed
 * - `insert`:  some new words added
 *
 * Note: there is no `replace` type. What was previously a replace is emitted
 * as a separate `delete` chunk followed by an `insert` chunk with the same
 * prefix/suffix context, so that all deletions can be applied before all
 * insertions.
 */
export interface ChangeChunk {
  type: 'delete' | 'insert';
  oldWords: string[];      // words to find/delete in the document  (empty for insert)
  newWords: string[];      // words to put in their place            (empty for delete)
  prefixWords: string[];   // up to 3 equal words BEFORE this change (for disambiguation)
  suffixWords: string[];   // up to 3 equal words AFTER  this change (for disambiguation)
}

/* ---------- helpers ---------- */

/** Split text into words on whitespace. */
function splitWords(text: string): string[] {
  return text.trim().split(/\s+/).filter(w => w.length > 0);
}

/* ---------- LCS-based word diff ---------- */

/**
 * Compute a word-level diff between `original` and `modified` using LCS.
 * Returns a list of merged DiffOps in forward (left-to-right) order.
 */
export function computeWordDiff(original: string, modified: string): DiffOp[] {
  const origWords = splitWords(original);
  const modWords  = splitWords(modified);
  const n = origWords.length;
  const m = modWords.length;

  // --- build LCS table ---
  const dp: number[][] = [];
  for (let i = 0; i <= n; i++) {
    dp[i] = new Array(m + 1).fill(0);
  }
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (origWords[i - 1] === modWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // --- backtrack to build raw ops (reverse order) ---
  const stack: DiffOp[] = [];
  let i = n;
  let j = m;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && origWords[i - 1] === modWords[j - 1]) {
      stack.push({ type: 'equal', words: [origWords[i - 1]] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'insert', words: [modWords[j - 1]] });
      j--;
    } else {
      stack.push({ type: 'delete', words: [origWords[i - 1]] });
      i--;
    }
  }

  // --- reverse to forward order, then merge consecutive same-type ops ---
  stack.reverse();

  const merged: DiffOp[] = [];
  for (const op of stack) {
    if (merged.length > 0 && merged[merged.length - 1].type === op.type) {
      merged[merged.length - 1].words.push(...op.words);
    } else {
      merged.push({ type: op.type, words: [...op.words] });
    }
  }

  return merged;
}

/**
 * Character-level diff for inline edit previews and precise insertions
 * (e.g. Hebrew text without spaces between words).
 */
export function computeCharDiff(original: string, modified: string): DiffOp[] {
  const origChars = [...original];
  const modChars = [...modified];
  const n = origChars.length;
  const m = modChars.length;

  const dp: number[][] = [];
  for (let i = 0; i <= n; i++) {
    dp[i] = new Array(m + 1).fill(0);
  }
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (origChars[i - 1] === modChars[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const stack: DiffOp[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && origChars[i - 1] === modChars[j - 1]) {
      stack.push({ type: 'equal', words: [origChars[i - 1]] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'insert', words: [modChars[j - 1]] });
      j--;
    } else {
      stack.push({ type: 'delete', words: [origChars[i - 1]] });
      i--;
    }
  }

  stack.reverse();

  const merged: DiffOp[] = [];
  for (const op of stack) {
    if (merged.length > 0 && merged[merged.length - 1].type === op.type) {
      merged[merged.length - 1].words.push(...op.words);
    } else {
      merged.push({ type: op.type, words: [...op.words] });
    }
  }

  return merged;
}

/**
 * Single-hunk character diff for live simple-edit (clause card / Word typing).
 *
 * Finds longest common prefix and suffix, then emits at most one delete and one
 * insert for the middle region. Unlike LCS char diff, this avoids alternating
 * insert/equal ops when the user types inside counterparty redline text — Word
 * keeps one growing w:ins sibling instead of splitting per character.
 */
export function computeContiguousCharDiff(original: string, modified: string): DiffOp[] {
  let prefixLen = 0;
  const minLen = Math.min(original.length, modified.length);
  while (prefixLen < minLen && original[prefixLen] === modified[prefixLen]) {
    prefixLen++;
  }

  let suffixLen = 0;
  const origRem = original.length - prefixLen;
  const modRem = modified.length - prefixLen;
  const maxSuffix = Math.min(origRem, modRem);
  while (
    suffixLen < maxSuffix &&
    original[original.length - 1 - suffixLen] === modified[modified.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const origMiddle = original.slice(prefixLen, original.length - suffixLen);
  const modMiddle = modified.slice(prefixLen, modified.length - suffixLen);

  const ops: DiffOp[] = [];
  const pushChars = (type: DiffOp['type'], text: string) => {
    if (text.length === 0) return;
    ops.push({ type, words: [...text] });
  };

  pushChars('equal', original.slice(0, prefixLen));
  pushChars('delete', origMiddle);
  pushChars('insert', modMiddle);
  pushChars('equal', original.slice(original.length - suffixLen));

  return ops;
}

/** Prefix / middle / suffix lengths for contiguous simple-edit (clause card typing). */
export function getContiguousEditRegion(
  original: string,
  modified: string,
): { prefixLen: number; origMiddleLen: number; insertLen: number; suffixLen: number } {
  let prefixLen = 0;
  const minLen = Math.min(original.length, modified.length);
  while (prefixLen < minLen && original[prefixLen] === modified[prefixLen]) {
    prefixLen++;
  }

  let suffixLen = 0;
  const origRem = original.length - prefixLen;
  const modRem = modified.length - prefixLen;
  const maxSuffix = Math.min(origRem, modRem);
  while (
    suffixLen < maxSuffix &&
    original[original.length - 1 - suffixLen] === modified[modified.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const origMiddleLen = original.length - prefixLen - suffixLen;
  const insertLen = modified.length - prefixLen - suffixLen;
  return { prefixLen, origMiddleLen, insertLen, suffixLen };
}

/* ---------- grouping into ChangeChunks ---------- */

/**
 * Group diff operations into ChangeChunks, attaching up to 3 words of
 * surrounding equal context for disambiguation during Word search.
 */
export function groupIntoChangeChunks(diffs: DiffOp[]): ChangeChunk[] {
  const chunks: ChangeChunk[] = [];
  let idx = 0;

  while (idx < diffs.length) {
    // skip equal blocks
    if (diffs[idx].type === 'equal') {
      idx++;
      continue;
    }

    // collect consecutive non-equal ops into one chunk
    const oldWords: string[] = [];
    const newWords: string[] = [];
    const start = idx;

    while (idx < diffs.length && diffs[idx].type !== 'equal') {
      if (diffs[idx].type === 'delete') {
        oldWords.push(...diffs[idx].words);
      } else {
        newWords.push(...diffs[idx].words);
      }
      idx++;
    }

    // prefix context (up to 3 words from the preceding equal block)
    const prefixWords: string[] = [];
    if (start > 0 && diffs[start - 1].type === 'equal') {
      prefixWords.push(...diffs[start - 1].words.slice(-3));
    }

    // suffix context (up to 3 words from the following equal block)
    const suffixWords: string[] = [];
    if (idx < diffs.length && diffs[idx].type === 'equal') {
      suffixWords.push(...diffs[idx].words.slice(0, 3));
    }

    // Emit delete first, then insert (never a combined replace).
    // This allows the caller to apply all deletes before all inserts.
    if (oldWords.length > 0) {
      chunks.push({ type: 'delete', oldWords, newWords: [], prefixWords, suffixWords });
    }
    if (newWords.length > 0) {
      chunks.push({ type: 'insert', oldWords: [], newWords, prefixWords, suffixWords });
    }
  }

  return chunks;
}
